/**
 * PipelineGraphView — visualization of a pipeline's workflow graph, with
 * optional live per-node editing.
 *
 * Presentational by default. The parent passes the backend serialized graph
 * (`{ nodes, edges, nodeConfigs, stateSchema }`); this component converts it to
 * React Flow shape (with elkjs auto-layout) via the shared
 * `convertBackendGraphToReactFlow` util and renders it inside a fixed-height
 * container using the existing `GraphFlowEditor`. The graph structure is always
 * locked (no drag/add/delete) — `readonly`.
 *
 * Node detail on click: handled by GraphFlowEditor itself — it opens the
 * existing NodeConfigModal (the Monaco-based "IDE" node modal). We do NOT roll
 * our own detail panel.
 *
 * Live editing: when `workflowUuid` is supplied (the deployed-pipeline Overview),
 * the modal opens EDITABLE. Saving a node diffs the edited config against the
 * node's current effective config and PATCHes only the delta to
 * `PATCH /workflows/{uuid}` as `nodeConfigOverrides[nodeId]`, so a few extra
 * instruction lines live-update the deployed pipeline without re-deploying.
 * Without `workflowUuid` (e.g. the marketplace preview) it stays view-only.
 *
 * Defensive: a falsy graph or one with no nodes renders a clean empty state
 * instead of crashing.
 */
import React, {
  useEffect, useState, useCallback, useMemo, useRef, useImperativeHandle, forwardRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { IonIcon } from '@ionic/react';
import { scanOutline, contractOutline } from 'ionicons/icons';
import {
  GraphFlowEditor,
  convertBackendGraphToReactFlow,
} from '../../graph-workflow-editor';
import { ModelNode, MemberNode } from '../../graph-workflow-editor/molecules';
import { withModelSatellites } from '../../graph-workflow-editor/utils/modelSatellites';
import AgentConfigModal from '../../molecules/AgentConfigModal/AgentConfigModal';
import {
  useAgentOptions, findAgentOptionValue,
  filterOptionsByAgents,
} from '../../../utils/agent-options';
import {
  VENDOR_ENV_KEYS, VENDOR_LABELS, agentSnapshotFor, projectSecretFor, unreadyNodes,
  unsetModelNodes,
} from '../../../utils/nodeModel';
import {
  dispatchedSlugs, envKeyMap, childIsReady,
} from '../../../utils/childAgentReadiness';
import { dispatchNodeMapOf } from '../../../utils/dispatchMarker';
import { withMemberSatellites } from '../../graph-workflow-editor/utils/memberSatellites';
import {
  capabilitiesOf, capKey, capRoute, handAddedMcp, memberRoute,
} from '../../../utils/agentCapabilities';
import { graphRunMarks, hopKey } from '../../graph-workflow-editor/utils/graphRuns';
import ConfirmModal from '../../molecules/ConfirmModal/ConfirmModal';
import api from '../../../services/api';
import { useAgents } from '../../../hooks/useAgents';
import './PipelineGraphView.css';

const EMPTY_FLOW = { nodes: [], edges: [], nodeConfigs: {}, stateSchema: null };
const EMPTY_FLAGGED = [];
const EMPTY_CHILD_READY = {};
const EMPTY_CHILD_ROWS = {};
const EMPTY_ACTIVE_RUNS = {};
const EMPTY_AGENT_ICONS = {};
const EMPTY_ACTIVE_STEPS = [];
// The node types that ARE steps of the authored graph — everything else on this
// canvas (a model disc, a member satellite) hangs off one. Nothing else is ever
// a place a run can be.
const GRAPH_NODE_TYPES = new Set(['workflowNode', 'decision', 'terminal']);
// Nothing in flight. A frozen singleton so an idle canvas re-renders to the
// SAME objects it rendered last time.
const EMPTY_RUN_MARKS = { nodes: {}, hops: {} };

/**
 * The origin a hop is KEYED by. The bracket out of the Start pill is keyed with
 * an EMPTY origin — byte-identical to how the self-host dashboard draws it
 * (`data-from=""`) — because that is the line `graphRunMarks` reaches for when a
 * run reports an origin this graph has no node for (the synthetic `setup`, or
 * the very first step, which reports none at all).
 */
const hopOrigin = (sourceId, startId) => (sourceId && sourceId === startId ? '' : (sourceId || ''));
const MODEL_NODE_TYPES = { modelNode: ModelNode, memberNode: MemberNode };
const EMPTY_CAP_ICONS = {};

// Keys a node override may carry (mirrors NodeConfigModal.handleSave's output).
const OVERRIDE_KEYS = ['prompt', 'extraPromptInstructions', 'tools', 'executeCode'];
const norm = (v) => (v === undefined ? null : v);

// Delta of `config` vs the node's current effective config. Only changed keys
// are sent so we never freeze a node's other (template-tracking) fields. Returns
// `{}` when nothing changed.
const overrideDelta = (config, base) => {
  const out = {};
  for (const k of OVERRIDE_KEYS) {
    if (JSON.stringify(norm(config[k])) !== JSON.stringify(norm(base?.[k]))) {
      out[k] = norm(config[k]);
    }
  }
  return out;
};

// `projectId` (optional): lets the node modal resolve a sub-workflow node's
// CHILD agent (deployed in the same project) and embed its graph.
// `embedded` (optional): render as a non-interactive embed (inside the node
// modal's sub-workflow panel) — node clicks disabled so modals never recurse,
// fullscreen button hidden. Pan/zoom stay enabled.
// `nodeConfigOverrides` (optional): the workflow row's RAW per-node override
// map. Load-bearing for the model disc: the `graph` this component receives has
// those overrides already MERGED into nodeConfigs, so `cfg.agent` alone cannot
// tell a vendor the TEMPLATE pinned from one the OPERATOR just picked — and
// reading it blind renders every just-set node as fixed-in-code, locked and
// unclickable. Absent → discs render, but a user's own pick reads as a lock.
const PipelineGraphView = forwardRef(({ graph, workflowUuid, webhookEnabled = false, onSaved, setToast, projectId = null, embedded = false, nodeConfigOverrides = null, modelBlocks = null, agentVendor = '', workflow = null, activeSteps = EMPTY_ACTIVE_STEPS }, ref) => {
  const canvasRef = useRef(null);
  const [flow, setFlow] = useState(EMPTY_FLOW);
  const [loading, setLoading] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  // The deployed agent's cron schedule (null = none). Fetched separately from
  // GET /workflows/{uuid}/schedule — the workflow detail object doesn't carry
  // it. Drives the Start node's trigger badge alongside `webhookEnabled`.
  const [schedule, setSchedule] = useState(null);
  // Bumped on every fullscreen toggle so the graph re-centers + zooms to fit
  // the resized canvas.
  const [fitSignal, setFitSignal] = useState(0);

  const hasGraph = !!(graph && Array.isArray(graph.nodes) && graph.nodes.length > 0);
  const editable = !!workflowUuid;
  // Model discs only on the deployed-agent surface: a marketplace preview has
  // no agent to hang a credential on, and the embedded child graph must not
  // offer edits that belong on the child's own page.
  const showModels = editable && !embedded;

  // Esc exits fullscreen; re-fit the view whenever the mode TOGGLES (skip the
  // initial mount so DefaultZoomOut owns first-load framing).
  const didMount = React.useRef(false);
  useEffect(() => {
    if (didMount.current) {
      setFitSignal((n) => n + 1);
    } else {
      didMount.current = true;
    }
    if (!fullscreen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setFullscreen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [fullscreen]);

  // Persist a single node's edits as a live override on the deployed pipeline.
  const handleSaveNodeConfig = useCallback(async (nodeId, config, baseConfig) => {
    if (!workflowUuid) return;
    const delta = overrideDelta(config, baseConfig);
    if (Object.keys(delta).length === 0) return; // nothing changed
    try {
      await api.patch(`/workflows/${encodeURIComponent(workflowUuid)}`, {
        nodeConfigOverrides: { [nodeId]: delta },
      });
      if (setToast) setToast({ show: true, message: 'Node updated — live on the deployed pipeline.', color: 'success' });
      if (onSaved) onSaved();
    } catch (err) {
      if (setToast) setToast({ show: true, message: `Failed to save node: ${err?.message || 'unknown error'}`, color: 'danger' });
    }
  }, [workflowUuid, onSaved, setToast]);

  // Fetch the deployed agent's cron schedule so the Start node can show a
  // trigger badge. Tied to workflowUuid; 404 = no schedule (a valid state).
  // Best-effort: a failure just means no cron badge, never blocks the graph.
  useEffect(() => {
    if (!workflowUuid) { setSchedule(null); return undefined; }
    let cancelled = false;
    api.get(`/workflows/${workflowUuid}/schedule`)
      .then((data) => { if (!cancelled) setSchedule(data?.schedule || null); })
      .catch(() => { if (!cancelled) setSchedule(null); });
    return () => { cancelled = true; };
  }, [workflowUuid]);

  // ── Per-node model ────────────────────────────────────────────────────
  // Every node that runs a model carries a disc showing WHICH model, and
  // clicking it picks the vendor + model + key. The two credential sources the
  // runtime consults, in ITS order: this agent's own env snapshot first, the
  // project's shared credential second.
  const { options: agentOptions } = useAgentOptions();
  const [agentEnvKeys, setAgentEnvKeys] = useState(null);   // {ENV_NAME: lastFour|true}
  const [project, setProject] = useState(null);
  const [picker, setPicker] = useState(null);               // {nodeId, label} | null
  // Node ids the Trigger check last pointed at. Held here (not derived) so the
  // ring appears only when the operator ASKS to run — an agent mid-setup
  // shouldn't be shouting at them the whole time they configure it.
  const [flagged, setFlagged] = useState(EMPTY_FLAGGED);
  // slug → true | false | null(unknown). A dispatch node runs ANOTHER agent,
  // and that agent dying for want of a model kills this run too, so the check
  // follows the dispatch edges. An unknown child is never blamed.
  const [childReady, setChildReady] = useState(EMPTY_CHILD_READY);
  // slug → the child's deployed ROW, reduced to what its satellite needs
  // ({uuid, workflowType, displayName, iconUrl}), or null when it is not
  // deployed here / could not be read. Filled from the very same row the
  // readiness pass already fetches, so drawing a started member costs no
  // request: the row carries the name the product uses for it everywhere else
  // (childAgentName), its own icon, and the uuid its run count is keyed by.
  const [childRows, setChildRows] = useState(EMPTY_CHILD_ROWS);
  // capKey (a UUID) → true | false | null(unknown). A CAPABILITY is checked by
  // its own uuid, never by slug: an INHERITED one is not in this agent's
  // binding ledger at all, and a member whose bare slug was taken is installed
  // under a namespaced one — so a slug lookup either misses it or lands on a
  // same-named stranger. Same predicate as a dispatch child (childIsReady),
  // which reads the MEMBER'S OWN declared model block and resolves its key BY
  // VENDOR through the member's env then the project credential: "declared but
  // not usable" therefore means one thing on every member, and a member that
  // needs no credential (browser) never warns.
  const [capReady, setCapReady] = useState(EMPTY_CHILD_READY);
  // capKey → the member row's own `iconUrl`. The satellite shows the MEMBER
  // AGENT'S OWN icon; '' falls back to the letter tile every icon-less agent on
  // this app already uses.
  const [capIcons, setCapIcons] = useState(EMPTY_CAP_ICONS);
  // The post-save "apply to all nodes?" offer, or null. Computed AFTER a pick
  // saves: {label, override, selfIds, children, childEnv, message}. Only ever
  // set when at least one other node is still unset — all set → no modal.
  const [applyAll, setApplyAll] = useState(null);
  const navigate = useNavigate();

  // Which nodes START which agents — from the ONE reader of the marker.
  const dispatchMap = useMemo(() => dispatchNodeMapOf(graph?.nodeConfigs), [graph]);
  const hasDispatch = Object.keys(dispatchMap).length > 0;

  // RUN COUNTS for the started members: uuid → activeRuns (running + queued).
  // Read off the project's agents list — the SAME React-Query key the Agents
  // page's Running pill reads, so this is one shared fetch, deduped by the
  // cache, not a second one; and `useAgents` already polls (8s) while any
  // agent has a run in flight, which is exactly when the picture changes.
  // Enabled only where satellites are drawn.
  // The same list also carries every deployed agent's ICON, which is what a
  // custom-MCP entry created by "Connect an agent" needs to show the agent it
  // names (see agentIconsByUuid below) — so the fetch is enabled whenever this
  // canvas has EITHER kind of satellite to resolve, not just dispatch.
  const handMcp = useMemo(() => handAddedMcp(workflow), [workflow]);
  const { data: agentRows } = useAgents(projectId, {
    enabled: showModels && (hasDispatch || handMcp.length > 0) && !!projectId,
  });
  // uuid → iconUrl for every deployed agent in the project. One derivation off
  // the list that is already in the cache; no second request.
  const agentIconsByUuid = useMemo(() => {
    if (!Array.isArray(agentRows) || agentRows.length === 0) return EMPTY_AGENT_ICONS;
    const out = {};
    for (const a of agentRows) {
      if (a && a.uuid && a.iconUrl) out[a.uuid] = a.iconUrl;
    }
    return out;
  }, [agentRows]);
  const activeRunsByUuid = useMemo(() => {
    if (!Array.isArray(agentRows) || agentRows.length === 0) return EMPTY_ACTIVE_RUNS;
    const out = {};
    for (const a of agentRows) {
      if (a && a.uuid) out[a.uuid] = Math.max(0, Number(a.activeRuns) || 0);
    }
    return out;
  }, [agentRows]);

  const loadAgentEnv = useCallback(async () => {
    if (!workflowUuid) { setAgentEnvKeys(null); return; }
    try {
      const data = await api.get(`/workflows/${encodeURIComponent(workflowUuid)}/env`);
      const map = {};
      for (const e of (data?.entries || [])) map[e.key] = e.lastFour || true;
      for (const k of (data?.keys || [])) if (!map[k]) map[k] = true;
      setAgentEnvKeys(map);
    } catch {
      // Unknown ≠ missing. Leaving this null makes vendorHasKey abstain rather
      // than paint every disc red on a transient failure.
      setAgentEnvKeys(null);
    }
  }, [workflowUuid]);

  useEffect(() => { loadAgentEnv(); }, [loadAgentEnv]);

  const loadProject = useCallback(async () => {
    if (!projectId) { setProject(null); return; }
    try {
      const data = await api.get(`/projects/${encodeURIComponent(projectId)}`);
      setProject(data?.project || data || null);
    } catch {
      setProject(null);
    }
  }, [projectId]);

  useEffect(() => { loadProject(); }, [loadProject]);

  const projectSecrets = project?.secrets || null;
  const projectVendor = project?.aiAgent || '';

  // Sub-agent readiness. Deliberately waits on `project` — the credentials it
  // judges by arrive asynchronously, and answering from an empty map would
  // call every child keyless. Each child is read from its OWN row + OWN env,
  // because that is where its keys live.
  useEffect(() => {
    if (!showModels || !projectId || !hasGraph) return undefined;
    if (project === null) return undefined;              // creds not in yet
    const slugs = dispatchedSlugs(graph);
    if (slugs.length === 0) {
      setChildReady(EMPTY_CHILD_READY);
      setChildRows(EMPTY_CHILD_ROWS);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(slugs.map(async (slug) => {
        try {
          const row = await api.get(
            `/projects/${encodeURIComponent(projectId)}/workflows/${encodeURIComponent(slug)}`,
          );
          if (!row || row.notInstalled === true) return [slug, null, null];
          let env = null;
          if (row.uuid) {
            env = await api.get(`/workflows/${encodeURIComponent(row.uuid)}/env`)
              .then(envKeyMap)
              .catch(() => null);
          }
          // The row answers EVERY question this pass asks — can this child run,
          // what is it called, what does it look like, and which uuid its run
          // count is keyed by. Keeping those fields here is why the member
          // satellite costs no second request (childAgentName(row, slug) is
          // applied where the satellite is built).
          // `agentVendor` here is THIS page's effective vendor (own row, else
          // inherited from ITS owning parent) — the AGENT_TYPE an in-process
          // child dispatch would actually run under.
          const kept = {
            uuid: row.uuid || '',
            workflowType: row.workflowType || slug,
            displayName: typeof row.displayName === 'string' ? row.displayName : '',
            iconUrl: row.iconUrl || '',
          };
          return [slug, childIsReady(row, env, projectSecrets, projectVendor, agentVendor), kept];
        } catch {
          return [slug, null, null];                      // unreadable → unjudged, no row
        }
      }));
      if (!cancelled) {
        setChildReady(Object.fromEntries(entries.map(([slug, ready]) => [slug, ready])));
        setChildRows(Object.fromEntries(entries.map(([slug, , row]) => [slug, row])));
      }
    })();
    return () => { cancelled = true; };
  }, [showModels, projectId, hasGraph, graph, project, projectSecrets, projectVendor, agentVendor]);

  // CAPABILITY readiness — the same check, over the rollup. Addressed by
  // workflowType (this app's router key) but KEYED by uuid, so an inherited
  // capability and a namespaced member both resolve to the row that backs them.
  const caps = useMemo(() => capabilitiesOf(workflow), [workflow]);
  useEffect(() => {
    if (!showModels || !projectId || caps.length === 0) {
      setCapReady(EMPTY_CHILD_READY); setCapIcons(EMPTY_CAP_ICONS); return undefined;
    }
    if (project === null) return undefined;              // creds not in yet
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(caps.map(async (m) => {
        const key = capKey(m);
        if (!m.workflowType) return [key, false, ''];    // declared, not installed
        try {
          const row = await api.get(
            `/projects/${encodeURIComponent(projectId)}/workflows/${encodeURIComponent(m.workflowType)}`,
          );
          if (!row || row.notInstalled === true) return [key, false, ''];
          let env = null;
          if (row.uuid) {
            env = await api.get(`/workflows/${encodeURIComponent(row.uuid)}/env`)
              .then(envKeyMap).catch(() => null);
          }
          // The MEMBER's own vendor, never this page's: each agent bills the
          // vendor its own row declares. Its own ICON comes off the same row —
          // the satellite draws the member agent's own badge, not a glyph.
          return [key, childIsReady(row, env, projectSecrets, projectVendor, ''), row.iconUrl || ''];
        } catch {
          return [key, null, ''];                        // unreadable → unjudged
        }
      }));
      if (cancelled) return;
      setCapReady(Object.fromEntries(entries.map(([k, r]) => [k, r])));
      setCapIcons(Object.fromEntries(entries.map(([k, , icon]) => [k, icon])));
    })();
    return () => { cancelled = true; };
  }, [showModels, projectId, caps, project, projectSecrets, projectVendor]);

  // Persist one node's model pick. Order matters: the CREDENTIAL lands before
  // the pick, so we can never leave a node pointed at a vendor whose key never
  // arrived — exactly the broken state this picker exists to prevent.
  const handleSaveNodeModel = useCallback(async (nodeId, body, locked = false) => {
    const vendor = typeof body.aiAgent === 'string' ? body.aiAgent : '';
    const aiModel = typeof body.aiModel === 'string' ? body.aiModel : '';
    const fresh = (typeof body.aiOAuthToken === 'string' && body.aiOAuthToken)
      || (typeof body.aiApiKey === 'string' && body.aiApiKey)
      || '';

    // 1. The project's shared credential — only when the user opted IN.
    if (fresh && body.saveToProject && projectId) {
      const secrets = {};
      if (vendor === 'codex') secrets.codexApiKey = fresh;
      else if (vendor === 'gemini') secrets.geminiApiKey = fresh;
      else if (/^sk-ant-oat01-/.test(fresh)) { secrets.aiOAuthToken = fresh; secrets.aiApiKey = ''; }
      else { secrets.aiApiKey = fresh; secrets.aiOAuthToken = ''; }
      await api.put(`/projects/${encodeURIComponent(projectId)}`, { secrets });
    }

    // 2. THIS agent's own env — ALWAYS, whatever the toggle said. The runtime
    //    reads the agent env before the project credential, so the snapshot is
    //    what keeps the node running if the project credential is later
    //    removed. Not swallowed: with the toggle off this is the only place
    //    the key lands, and a green toast over a key that went nowhere is the
    //    worst outcome available.
    // 2b. ADOPT: the field was left on the pre-filled PROJECT key and the user
    //     clicked Save anyway — that click is the consent that attaches the
    //     project credential to THIS agent (founder, 2026-08-27: "user has to
    //     open it, see the populated project token, click Save"). The browser
    //     never holds the plaintext, so the server copies it: PATCH
    //     /env/<slot> {fromProjectVendor}. Only when this agent has no snapshot
    //     of its own for the vendor (a snapshot is already "set" — nothing to
    //     adopt) and the project actually has one to give.
    const adopt = !fresh && vendor
      && !agentSnapshotFor(vendor, agentEnvKeys)
      && !!projectSecretFor(vendor, projectSecrets, projectVendor);
    if ((fresh || adopt) && vendor) {
      let envKey = fresh && /^sk-ant-oat01-/.test(fresh)
        ? 'CLAUDE_CODE_OAUTH_TOKEN'
        : (VENDOR_ENV_KEYS[vendor] || [])[0];
      if (envKey) {
        const resp = await api.patch(
          `/workflows/${encodeURIComponent(workflowUuid)}/env/${encodeURIComponent(envKey)}`,
          fresh ? { value: fresh } : { fromProjectVendor: vendor },
        );
        // The server routes a Claude adoption by the token's SHAPE and answers
        // with the slot it wrote — clear the siblings of THAT one.
        if (resp?.key) envKey = resp.key;
        // …and CLEAR the vendor's OTHER credential slots, or the new key can be
        // silently ignored. `enforceSingleClaudeCredential` makes OAuth WIN: a
        // stale CLAUDE_CODE_OAUTH_TOKEN causes the run to DROP the fresh
        // ANTHROPIC_API_KEY we just wrote — so replacing a dead OAuth token
        // with a working API key would show green here and keep running on the
        // dead one. One credential per vendor, the same rule the project
        // secrets path already follows (`aiOAuthToken` / `aiApiKey` clear each
        // other). Best-effort: the key that matters has already landed.
        for (const stale of (VENDOR_ENV_KEYS[vendor] || [])) {
          if (stale === envKey) continue;
          try {
            await api.delete(`/workflows/${encodeURIComponent(workflowUuid)}/env/${encodeURIComponent(stale)}`);
          } catch { /* absent → nothing to clear, which is the desired state */ }
        }
      }
    }

    // 3. The pick itself → the same per-node override map custom prompts use.
    //    Live: it takes effect on the next run, with no redeploy.
    //
    //    SKIPPED for a template-fixed node: there was no pick to make, and
    //    writing one would be worse than pointless — an override the engine
    //    ranks ABOVE the code that is actually choosing the model, so the
    //    graph would start claiming a model the run never uses.
    // The PATCH response's `modelPins` report — the backend fans a node pin
    // out to every bound child itself (workflows.js → model-pin-fanout), and
    // this report is the only truthful record of what it wrote. Read-only
    // here: the UI never writes children (two-places rule).
    let pinFanout = null;
    if (!locked) {
      // No model picked ⇒ write NO model key. This used to write a fallback
      // sentinel, which the engine then resolved to the vendor's registry
      // default — a node that looked unset in the UI and ran sonnet in
      // production. (Same deletion as the self-host dashboard.)
      const override = { agent: vendor || null };
      if (aiModel) override.model = aiModel;
      const resp = await api.patch(`/workflows/${encodeURIComponent(workflowUuid)}`, {
        nodeConfigOverrides: { [nodeId]: override },
      });
      pinFanout = resp?.modelPins || null;
    }

    // This node is no longer what the Trigger check was complaining about, so
    // stop ringing it. Only THIS one — any other node still unset stays lit, or
    // clearing them all would read as "everything's fine now" after fixing one
    // of three. (The flag is our own marker, not derived state: without this it
    // glowed forever, over a node the operator had already dealt with.)
    setFlagged((prev) => {
      if (!prev.includes(nodeId)) return prev;
      const next = prev.filter((id) => id !== nodeId);
      return next.length ? next : EMPTY_FLAGGED;
    });

    await loadAgentEnv();
    if (body.saveToProject) await loadProject();

    // ── Offer "apply to all"? THIS AGENT'S OWN nodes only. ────────────────
    // Sub-agents are no longer offered (or written) here: the node-pin PATCH
    // above fans the pin out to every bound child SERVER-SIDE (backend
    // workflows.js → services/model-pin-fanout.js — the one child writer;
    // explicit picks never overwritten), so a second client-side child walk
    // was the two-places bug: it double-wrote children, and once the server
    // fan-out landed it counted zero and the offer silently vanished. SELF
    // stays this explicit question — the server deliberately never touches
    // the agent's own other nodes. `pinFanout` is only NARRATED, never
    // re-written.
    if (!locked && vendor && aiModel) {
      const override = { agent: vendor, model: aiModel };
      const label = `${VENDOR_LABELS[vendor] || vendor} · ${aiModel}`;
      const fanKids = Array.isArray(pinFanout?.children) ? pinFanout.children : [];
      const fanNodes = fanKids.reduce((n, c) => n + (Array.isArray(c.nodes) ? c.nodes.length : 0), 0);
      const selfIds = unsetModelNodes({
        graph, overrides: nodeConfigOverrides, modelBlocks, excludeId: nodeId,
      });
      if (selfIds.length) {
        const childNote = fanKids.length
          ? ' (Its sub-agents already inherited this pick automatically.)'
          : '';
        setApplyAll({
          label,
          override,
          selfIds,
          total: selfIds.length,
          message: `${selfIds.length} other node${selfIds.length > 1 ? 's' : ''} on this agent still ${selfIds.length > 1 ? 'have' : 'has'} no model. Use ${label} for all of them?${childNote}`,
        });
      } else if (fanNodes) {
        // Nothing left to ask — but the server DID fan the pick out, and doing
        // that silently reads as "the follow-up modal disappeared".
        setToast?.({
          show: true, isOpen: true, color: 'success',
          message: `${label} also inherited by ${fanNodes} node${fanNodes > 1 ? 's' : ''} across ${fanKids.length} sub-agent${fanKids.length > 1 ? 's' : ''}.`,
        });
      }
    }
  }, [workflowUuid, projectId, loadAgentEnv, loadProject, graph, nodeConfigOverrides, modelBlocks, setToast]);

  // Apply the just-saved pick to this agent's other unset nodes — ONE PATCH on
  // its own row. Children are the backend fan-out's job (already done by the
  // save's PATCH); writing them here again would be the two-writers bug.
  const applyModelToAll = useCallback(async () => {
    const t = applyAll;
    if (!t) return;
    setApplyAll(null);
    try {
      const payload = {};
      for (const id of t.selfIds) payload[id] = t.override;
      await api.patch(`/workflows/${encodeURIComponent(workflowUuid)}`, { nodeConfigOverrides: payload });
      setToast?.({
        show: true, isOpen: true, color: 'success',
        message: `${t.total} node${t.total > 1 ? 's' : ''} now run ${t.label}.`,
      });
    } catch (err) {
      setToast?.({
        show: true, isOpen: true, color: 'danger',
        message: `Couldn't apply the model: this agent (${err?.message || 'failed'}).`,
      });
    }
    if (onSaved) onSaved();
  }, [applyAll, workflowUuid, onSaved, setToast]);

  // The Trigger check. Deliberately NOT a disabled button: a dead control tells
  // you nothing about WHY. The button stays live and answers by scrolling to
  // the graph, ringing every node that can't run, and naming them in a toast —
  // and each of those rings is the very control that fixes it.
  //
  // Returns true when it BLOCKED (the caller must not open the trigger modal).
  const flagUnreadyModels = useCallback(() => {
    if (!showModels || !hasGraph) return false;
    const bad = unreadyNodes({
      graph,
      overrides: nodeConfigOverrides,
      agentEnvKeys,
      projectSecrets,
      projectVendor,
      modelBlocks,
      agentVendor,
    });
    // …and the sub-agents this graph dispatches: a child dying for want of a
    // model kills the parent's run too.
    const badChildren = Object.keys(childReady).filter((slug) => childReady[slug] === false);
    if (bad.length === 0 && badChildren.length === 0) {
      setFlagged(EMPTY_FLAGGED);
      return false;
    }
    setFlagged(bad.map((b) => b.id));
    canvasRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Short on purpose. The glowing discs already say WHERE — repeating every
    // node id and its reason in the toast made a line too long to read, for
    // information the graph is showing better. Name the one node when there IS
    // one; otherwise just count them.
    const noKey = bad.filter((b) => b.why === 'no key').length;
    setToast?.({
      show: true,
      isOpen: true,
      color: 'warning',
      message: bad.length === 0
        ? `Finish ${badChildren.join(', ')} first — open the red sub-agent.`
        : bad.length === 1
          ? `${bad[0].id} needs ${noKey ? 'a key' : 'a model'} — click it on the graph.`
          : `${bad.length} nodes need a model — click each on the graph.`,
    });
    return true;
  }, [showModels, hasGraph, graph, nodeConfigOverrides, agentEnvKeys,
    projectSecrets, projectVendor, modelBlocks, agentVendor, childReady, setToast]);

  useImperativeHandle(ref, () => ({ flagUnreadyModels }), [flagUnreadyModels]);

  // Normalized trigger summary injected into the Start node's data.
  const triggers = useMemo(() => ({
    cron: schedule?.cron || null,
    timezone: schedule?.timezone || null,
    webhook: !!webhookEnabled,
  }), [schedule, webhookEnabled]);

  // Inject the trigger summary into the Start terminal node so TerminalNode can
  // render the badge. Recomputes when the schedule resolves (badge appears once
  // the async fetch lands; syncFromProps re-syncs GraphFlowEditor).
  //
  // Nothing else is rewritten here. A node that dispatches child agents is a
  // STEP like any other and opens ITSELF (its code / prompt) on click; the
  // agents it starts are drawn as member satellites hanging off it — see
  // `withMemberSatellites` below — and each of THOSE carries the pointer to
  // the other agent, its readiness mark and its running state. (A synthesized
  // member BOX used to be rewritten here; the founder replaced it with the
  // satellite, 2026-08-25.)
  const nodesWithTriggers = useMemo(() => {
    if (!Array.isArray(flow.nodes)) return flow.nodes;
    return flow.nodes.map((n) => {
      if (n.type === 'terminal' && n.data?.terminal === 'start') {
        return { ...n, data: { ...n.data, triggers } };
      }
      return n;
    });
  }, [flow.nodes, triggers]);

  // ── THE GRAPH ITSELF RUNS ─────────────────────────────────────────────
  // Founder, 2026-08-26: "can we do the same fiber effect on the normal graph
  // as well?? … when the graph itself runs and travels between nodes??"
  //
  // A STEP with runs on it gets the treatment a running MEMBER satellite
  // already gets: one sheet of paper per run behind its tile, a glowing name,
  // and light travelling down the line it just arrived on. The DECISION of
  // what to light is `graphRunMarks` — pure data, no React — so this surface
  // and the self-host dashboard answer the same question the same way.
  //
  // What this canvas actually DREW is asked of the laid-out nodes/edges, never
  // of the serialized graph: a node the converter dropped can then never be
  // "lit" off-screen. The Start pill is the ORIGIN OF THE BRACKET, keyed with
  // an EMPTY origin exactly as the self-host twin keys it (`'' > <first>`), so
  // a run at the very first step — which reports `from: ''` or the synthetic
  // `setup` — lights that one line instead of every line into the node.
  const runMarks = useMemo(() => {
    const nodes = Array.isArray(nodesWithTriggers) ? nodesWithTriggers : [];
    const startNode = nodes.find((n) => n.type === 'terminal' && n.data?.terminal === 'start');
    const startId = startNode ? startNode.id : '';
    if (!Array.isArray(activeSteps) || activeSteps.length === 0) {
      return { ...EMPTY_RUN_MARKS, startId };
    }
    const nodeIds = {};
    for (const n of nodes) if (n && GRAPH_NODE_TYPES.has(n.type)) nodeIds[n.id] = true;
    const hopsInto = {};
    for (const e of (Array.isArray(flow.edges) ? flow.edges : [])) {
      if (!e || !nodeIds[e.target]) continue;
      (hopsInto[e.target] = hopsInto[e.target] || []).push(hopOrigin(e.source, startId));
    }
    return { ...graphRunMarks(activeSteps, nodeIds, hopsInto, startId), startId };
  }, [activeSteps, nodesWithTriggers, flow.edges]);

  // A running step is a pointer at THIS agent's runs, so a click hands over the
  // LIST of them — several runs can share one node and the canvas cannot say
  // which one you meant (founder, 2026-08-26: "we can't decide if more than one
  // is running?"). Same ruling AND THE SAME RESOLVER a running member satellite
  // uses, so the two clicks can never drift apart.
  const openOwnRuns = useCallback(() => {
    const r = memberRoute(projectId, workflow, 1)
      || (projectId && workflowUuid
        ? `/projects/${encodeURIComponent(projectId)}/agents/${encodeURIComponent(workflowUuid)}?tab=activity`
        : '');
    if (r) navigate(r);
  }, [projectId, workflow, workflowUuid, navigate]);

  // Stamp the marks onto the nodes the run stack reads (`data.activeRuns`) and
  // the edges the fibre reads (`data.activeRuns`, the same key a member
  // connector carries — one number, two readers).
  //
  // IDLE IS PIXEL-STABLE BY CONSTRUCTION: with nothing in flight the SAME array
  // instances come straight back out, so an idle graph is not merely equal to
  // the graph that was always drawn, it is that graph.
  const nodesWithRuns = useMemo(() => {
    if (!Object.keys(runMarks.nodes).length) return nodesWithTriggers;
    return nodesWithTriggers.map((n) => {
      const runs = runMarks.nodes[n.id] || 0;
      if (!runs || !GRAPH_NODE_TYPES.has(n.type)) return n;
      return {
        ...n,
        data: {
          ...n.data,
          activeRuns: runs,
          // An IDLE node keeps today's behaviour untouched (it opens its own
          // config); only a LIT one owns its click.
          onNodeClick: openOwnRuns,
        },
      };
    });
  }, [nodesWithTriggers, runMarks, openOwnRuns]);

  const edgesWithRuns = useMemo(() => {
    if (!Object.keys(runMarks.hops).length) return flow.edges;
    return flow.edges.map((e) => {
      const runs = runMarks.hops[hopKey(hopOrigin(e.source, runMarks.startId), e.target)] || 0;
      if (!runs) return e;
      return { ...e, data: { ...(e.data || {}), activeRuns: runs } };
    });
  }, [flow.edges, runMarks]);

  // The graph the editor actually renders: the flow plus one model disc per
  // model-running node. Only on the deployed-agent surface — a marketplace
  // preview has no agent to hang a credential on, and the embedded child graph
  // must not offer edits that belong on the child's own page.
  const withModels = useMemo(() => {
    if (!showModels) return { nodes: nodesWithRuns, edges: edgesWithRuns };
    const withM = withModelSatellites(nodesWithRuns, edgesWithRuns, graph, {
      overrides: nodeConfigOverrides,
      agentEnvKeys,
      projectSecrets,
      projectVendor,
      modelBlocks,
      agentVendor,
      flagged,
      onPick: (nodeId, label, state) => setPicker({ nodeId, label, state }),
    });
    // …and the MEMBERS, in the same vocabulary — CONSUMED (`uses`) and
    // STARTED (`starts`) alike. Members share the model satellite's lane, so a
    // node carrying several puts them side by side rather than on top of each
    // other.
    return withMemberSatellites(withM.nodes, withM.edges, {
      capabilities: caps,
      customMcp: handMcp,
      capReady,
      capIcons,
      agentIconsByUuid,
      onOpenMember: (m) => { const r = capRoute(projectId, m); if (r) navigate(r); },
      // A hand-added MCP server is not an agent and has no page. Its row lives
      // on this agent's own settings, which is where it can be edited.
      onOpenSettings: () => {
        if (workflowUuid && projectId) {
          navigate(`/projects/${encodeURIComponent(projectId)}/agents/${encodeURIComponent(workflowUuid)}?tab=settings`);
        }
      },
      // The agents each node STARTS. Rows + readiness come from the readiness
      // pass above (one fetch per child, already made); the run count from the
      // agents list the Running pill reads (one query, already polling).
      dispatch: {
        map: dispatchMap,
        counts: Object.fromEntries(Object.entries(graph?.nodeConfigs || {}).map(([nodeId, config]) => [
          nodeId,
          (config?.collaborationInstances && typeof config.collaborationInstances === 'object')
            ? config.collaborationInstances : {},
        ])),
        rows: childRows,
        ready: childReady,
        activeRuns: activeRunsByUuid,
        // A started member's page of interest is its ACTIVITY — the runs this
        // step started (founder: "activity page not details page").
        // RUNNING → its Activity (several runs, the canvas cannot say which);
        // IDLE → the member's own page. memberRoute owns that split.
        onOpen: (row) => {
          const r = memberRoute(projectId, row, activeRunsByUuid[row?.uuid] || 0);
          if (r) navigate(r);
        },
        // Declared by the template, not deployed in this project: there is no
        // page to go to, so say so where the click happened.
        onMissing: (slug) => setToast?.({
          show: true, isOpen: true, color: 'warning',
          message: `Sub-agent not deployed — ${slug} isn't deployed in this project yet.`,
        }),
      },
    });
  }, [showModels, nodesWithRuns, edgesWithRuns, graph, nodeConfigOverrides,
    agentEnvKeys, projectSecrets, projectVendor, modelBlocks, agentVendor, flagged,
    caps, handMcp, capReady, capIcons, agentIconsByUuid, projectId, navigate, workflowUuid,
    dispatchMap, childRows, childReady, activeRunsByUuid, setToast]);

  // Seed the picker with what the node runs TODAY, and show the credential we
  // would actually use: this agent's own snapshot first (masked to its last
  // four), else the project's. So a saved key reads back as saved instead of
  // asking again.
  // A template-FIXED node opens the same modal with the model read-only: the
  // template chose what runs, so there is nothing to pick — but it cannot
  // supply the credential, so the key field is exactly as live as anywhere
  // else. Without this the node was a dead end: not clickable, and skipped by
  // the trigger check, so a fixed-model agent could never be given its key.
  const isLockedPick = picker?.state?.kind === 'locked';

  const pickerSeed = useMemo(() => {
    if (!picker) return { value: '', options: [], keysByAgent: {}, keySourceByAgent: {}, vendorsWithProjectKey: [] };
    const ov = nodeConfigOverrides?.[picker.nodeId] || {};
    const keysByAgent = {};
    // WHICH row each mask came from — 'agent' (this agent's own env snapshot),
    // 'project' (the shared project secret), or 'agent+project' (both; the
    // agent's copy wins at run time). A bare mask inside this input reads as
    // "this project has a key" even when the mask is the agent's own snapshot
    // and the project has nothing (founder, 2026-08-24) — the field's helper
    // line names the scope. Never more than the mask travels.
    const keySourceByAgent = {};
    const vendorsWithProjectKey = [];
    for (const v of Object.keys(VENDOR_ENV_KEYS)) {
      const snap = agentSnapshotFor(v, agentEnvKeys);
      const proj = projectSecretFor(v, projectSecrets, projectVendor);
      if (proj) vendorsWithProjectKey.push(v);
      // A mask (contains '...') is what tells the field "a key is stored —
      // leave me blank to keep it". `true` means we know a key exists but not
      // its tail, so render a tail-less mask rather than an empty field.
      if (snap) {
        keysByAgent[v] = typeof snap === 'string' ? `sk-...${snap}` : 'sk-...';
        keySourceByAgent[v] = proj ? 'agent+project' : 'agent';
      } else if (proj) {
        keysByAgent[v] = proj;
        keySourceByAgent[v] = 'project';
      }
    }
    // NO "Project default" sentinel. That option means "this row picks no
    // vendor" — and a node with no vendor has no credential field either
    // (`needsKey = Boolean(vendor)`), so opening on it showed a model dropdown
    // with nowhere to put the key. There IS no agent-wide default any more:
    // every node lands on a concrete vendor, so the picker only offers those.
    const options = filterOptionsByAgents(agentOptions, []);
    // Open on what the node runs today — and on NOTHING otherwise. The old
    // fallbacks (project vendor's first option, then options[0]) preselected a
    // model nobody chose, one Save away from storing it as a deliberate pick.
    const value = findAgentOptionValue({ aiAgent: ov.agent, aiModel: ov.model }, options) || '';
    // Fixed node: the vendor is whatever will actually run, and the model is
    // whatever the disc already resolved — not a dropdown value.
    if (picker.state?.kind === 'locked') {
      return {
        // Vendor only — there is no model VALUE to carry (the template's code
        // owns the model). parseAgentOption reads the vendor off a bare name.
        value: picker.state.vendor || '',
        options,
        keysByAgent,
        keySourceByAgent,
        vendorsWithProjectKey,
      };
    }
    return { value, options, keysByAgent, keySourceByAgent, vendorsWithProjectKey };
  }, [picker, nodeConfigOverrides, agentEnvKeys, projectSecrets, projectVendor, agentOptions]);

  // Convert backend graph → React Flow shape (async: elkjs auto-layout).
  // Guarded against out-of-order resolutions if `graph` changes mid-flight.
  useEffect(() => {
    let cancelled = false;
    if (!hasGraph) {
      setFlow(EMPTY_FLOW);
      return undefined;
    }
    setLoading(true);
    convertBackendGraphToReactFlow(graph)
      .then((result) => {
        if (!cancelled) setFlow(result || EMPTY_FLOW);
      })
      .catch(() => {
        if (!cancelled) setFlow(EMPTY_FLOW);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [graph, hasGraph]);

  if (!hasGraph) {
    return (
      <div className="pipeline-graph-view pipeline-graph-view--empty">
        <div className="pipeline-graph-view__empty-text">
          Graph not available for this pipeline.
        </div>
      </div>
    );
  }

  return (
    <div className={`pipeline-graph-view${fullscreen ? ' pipeline-graph-view--fullscreen' : ''}${embedded ? ' pipeline-graph-view--embedded' : ''}`}>
      <div className="pipeline-graph-view__canvas" ref={canvasRef}>
        {loading && (
          <div className="pipeline-graph-view__loading">Laying out graph…</div>
        )}
        {!embedded && (
          <button
            type="button"
            className="pipeline-graph-view__fs-btn"
            onClick={() => setFullscreen((v) => !v)}
            aria-label={fullscreen ? 'Exit full screen' : 'Full screen'}
            data-tooltip={fullscreen ? 'Exit full screen' : 'Full screen'}
          >
            <IonIcon icon={fullscreen ? contractOutline : scanOutline} />
          </button>
        )}
        <GraphFlowEditor
          readonly
          syncFromProps
          customNodeTypes={MODEL_NODE_TYPES}
          initialNodes={withModels.nodes}
          initialEdges={withModels.edges}
          nodeConfigs={flow.nodeConfigs}
          stateSchema={flow.stateSchema}
          showResetPanel={false}
          showGraphInfoPanel={false}
          fitSignal={fitSignal}
          nodeConfigEditable={editable}
          onSaveNodeConfig={editable ? handleSaveNodeConfig : null}
          projectId={projectId}
          workflowUuid={workflowUuid}
          setToast={setToast}
          onCollaborationSaved={onSaved}
          nodesClickable={!embedded}
        />
      </div>

      {picker && (
        <AgentConfigModal
          isOpen
          onClose={() => setPicker(null)}
          variant="pipeline"
          title={`${picker.label} — ${isLockedPick ? 'key' : 'model'}`}
          // The vendor is already named by the key field's own label right
          // below ("Anthropic API key"), so repeating it here just made the
          // line longer. Show it only when the model itself is unknowable and
          // the vendor is the only thing we can say.
          fixedModelLabel={isLockedPick
            ? (picker.state.label === 'Fixed' && picker.state.vendor
              ? `Fixed · ${picker.state.vendor}`
              : picker.state.label)
            : ''}
          agentOptions={pickerSeed.options}
          seedValue={pickerSeed.value}
          seedKeysByAgent={pickerSeed.keysByAgent}
          keySourceByAgent={pickerSeed.keySourceByAgent}
          // The opt-in appears ONLY for a vendor the project has no credential
          // for — there is nothing to ask once the project already has one —
          // and it is never pre-checked: writing a key into shared project
          // settings is the user's call, not our default.
          vendorsWithProjectKey={pickerSeed.vendorsWithProjectKey}
          saveToProjectLabel="Also save as this project's default key"
          onSave={(body) => handleSaveNodeModel(picker.nodeId, body, isLockedPick)}
          onSaved={onSaved}
          setToast={setToast}
        />
      )}

      {applyAll && (
        <ConfirmModal
          open
          onClose={() => setApplyAll(null)}
          title="Apply to all nodes?"
          primaryLabel="Apply to all"
          onPrimary={applyModelToAll}
          secondaryLabel="Skip"
        >
          <p>{applyAll.message}</p>
        </ConfirmModal>
      )}
    </div>
  );
});

export default PipelineGraphView;
